import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { api } from '../api';
import { clearAuthToken } from '../api/base';

const AuthContext = createContext(null);

const API_BASE = import.meta.env.VITE_API_URL || '';

/** Decode a JWT payload without verification (client-side only). */
function decodeJwtPayload(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

function isJwt(token) {
  return typeof token === 'string' && token.startsWith('ey');
}

function isJwtExpired(token) {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return true;
  return Date.now() / 1000 > payload.exp;
}

const persistBackofficeSession = ({ backofficeToken, portalToken, user }) => {
  if (backofficeToken) {
    localStorage.setItem('authToken', backofficeToken);
    localStorage.setItem('token', backofficeToken);
  }
  if (portalToken) {
    localStorage.setItem('portalToken', portalToken);
  } else {
    localStorage.removeItem('portalToken');
  }
  if (user) {
    localStorage.setItem('backofficeUser', JSON.stringify(user));
    localStorage.setItem('staff', JSON.stringify(user));
  }
};

const persistAuthPayload = ({ accessToken, refreshToken, user }) => {
  if (accessToken) {
    localStorage.setItem('authToken', accessToken);
    localStorage.setItem('token', accessToken);
  }
  if (refreshToken) {
    localStorage.setItem('refreshToken', refreshToken);
  }
  if (user) {
    localStorage.setItem('staff', JSON.stringify(user));
  }
};

export const AuthProvider = ({ children }) => {
  const [state, setState] = useState({
    status: 'idle',
    user: null,
    venue: null,
    viewingVenue: null,
    roles: [],
    device: null,
  });

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setState((prev) => ({ ...prev, status: 'signedOut', user: null, venue: null, viewingVenue: null, roles: [] }));
      return;
    }

    // ── JWT path (backoffice web login) ──
    if (isJwt(token)) {
      if (isJwtExpired(token)) {
        clearAuthToken();
        localStorage.removeItem('portalToken');
        localStorage.removeItem('backofficeUser');
        localStorage.removeItem('staff');
        setState({ status: 'signedOut', user: null, venue: null, viewingVenue: null, roles: [], device: null });
        return;
      }
      const cached = localStorage.getItem('backofficeUser');
      const user = cached ? JSON.parse(cached) : decodeJwtPayload(token);
      setState({
        status: 'ready',
        user,
        venue: user?.venues?.[0] ?? null,
        viewingVenue: null,
        roles: user?.roles ?? [user?.role].filter(Boolean),
        device: null,
      });
      return;
    }

    // ── Opaque token path (POS session) ──
    setState((prev) => ({ ...prev, status: 'loading' }));
    try {
      const profile = await api.auth.me();
      setState({
        status: 'ready',
        user: profile.user,
        venue: profile.venue,
        roles: profile.roles ?? [],
        device: profile.device ?? null,
      });
    } catch (error) {
      console.error('Failed to bootstrap auth:', error);
      clearAuthToken();
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('staff');
      setState({ status: 'signedOut', user: null, venue: null, viewingVenue: null, roles: [], device: null });
    }
  }, []);

  useEffect(() => {
    bootstrap().catch((err) => console.error('Bootstrap error', err));
  }, [bootstrap]);

  // ── Auto-refresh JWT before it expires (web stays logged in all day) ──
  useEffect(() => {
    // Refresh every 6 hours (JWT lasts 8h, so we refresh well before expiry)
    const REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000;

    const refreshToken = async () => {
      const token = localStorage.getItem('authToken');
      if (!token || !isJwt(token)) return; // Only refresh JWTs

      try {
        const res = await fetch(`${API_BASE}/api/auth/backoffice/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.backofficeToken) {
          localStorage.setItem('authToken', data.backofficeToken);
          localStorage.setItem('token', data.backofficeToken);
        }
        if (data.portalToken) {
          localStorage.setItem('portalToken', data.portalToken);
        }
        console.log('[auth] Token refreshed silently');
      } catch {
        // Refresh failed — will try again next interval
      }
    };

    const interval = setInterval(refreshToken, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  /** Email + password login for the back-office web app. */
  const loginWithPassword = useCallback(async (email, password) => {
    const res = await fetch(`${API_BASE}/api/auth/backoffice/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Login failed');

    persistBackofficeSession({
      backofficeToken: data.backofficeToken,
      portalToken: data.portalToken,
      user: data.user,
    });

    setState({
      status: 'ready',
      user: data.user,
      venue: data.user?.venues?.[0] ?? null,
      viewingVenue: null,
      roles: data.user?.roles ?? [data.user?.role].filter(Boolean),
      device: null,
    });
    return data;
  }, []);

  /** Exchange a one-time launch token (SSO from quantbar.net). */
  const loginWithLaunchToken = useCallback(async (token) => {
    const res = await fetch(`${API_BASE}/api/auth/backoffice/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Exchange failed');

    persistBackofficeSession({
      backofficeToken: data.backofficeToken,
      portalToken: data.portalToken,
      user: data.user,
    });

    setState({
      status: 'ready',
      user: data.user,
      venue: data.user?.venues?.[0] ?? null,
      viewingVenue: null,
      roles: data.user?.roles ?? [data.user?.role].filter(Boolean),
      device: null,
    });
    return data;
  }, []);

  /** Legacy POS login (venue PIN + staff PIN — still used by desktop/mobile). */
  const login = useCallback(
    async ({ venuePin, pin, deviceToken, deviceLabel, email }) => {
      const response = await api.auth.login({ venuePin, pin, deviceToken, deviceLabel, email });
      persistAuthPayload({
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        user: response.user,
      });
      setState({
        status: 'ready',
        user: response.user,
        venue: response.venue,
        roles: response.user?.roles ?? [],
        device: response.device ?? null,
      });
      return response;
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      // Only call server logout for opaque-token sessions
      const token = localStorage.getItem('authToken');
      if (token && !isJwt(token)) {
        await api.auth.logout();
      }
    } catch (error) {
      console.warn('Logout error', error);
    } finally {
      clearAuthToken();
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('portalToken');
      localStorage.removeItem('backofficeUser');
      localStorage.removeItem('staff');
      setState({ status: 'signedOut', user: null, venue: null, viewingVenue: null, roles: [], device: null });
    }
  }, []);

  const setViewingVenue = useCallback((venue) => {
    setState((prev) => ({ ...prev, viewingVenue: venue }));
  }, []);

  const activeVenue = state.viewingVenue ?? state.venue;

  const value = useMemo(
    () => ({
      ...state,
      activeVenue,
      login,
      loginWithPassword,
      loginWithLaunchToken,
      logout,
      refresh: bootstrap,
      setViewingVenue,
    }),
    [state, activeVenue, login, loginWithPassword, loginWithLaunchToken, logout, bootstrap, setViewingVenue],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};
